import React, { useEffect, useState, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useScreenInit } from './useScreenInit.js';
import { AboutUs } from './pages/AboutUs';
import { Services } from './pages/Services';
import { ServiceDetails } from './pages/ServiceDetails';
import { ContactUs } from './pages/ContactUs';
import { Blog } from './pages/Blog';
import { BlogDetails } from './pages/BlogDetails';
import { Menu, X, Phone, Mail, MapPin, Clock, Users, Heart, Coffee, Tv, Utensils, Calendar, ChevronRight, Star, Instagram, Facebook, Linkedin } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
// --- Utilities ---
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
// --- Components ---
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isAboutPage = location.pathname === '/about';
  const isServicesPage = location.pathname === '/services';
  const isContactPage = location.pathname === '/contact';
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <nav className={cn('fixed top-0 left-0 right-0 z-50 transition-all duration-500 px-4 md:px-8 py-1', isScrolled ? 'bg-white/80 backdrop-blur-lg border-b border-warmGray/10 py-1' : 'bg-transparent')} data-id="element-0">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4" data-id="element-1">
        <Link to="/" className="flex-shrink-0" data-id="element-2">
          <img src="https://cdn.magicpatterns.com/uploads/mhUq4NyA1Ppgv9PPq8CSnF/ChatGPT_Image_Jun_30,_2026,_11_49_14_PM.png" alt="Elisabeth's House Logo" className="h-16 md:h-20 lg:h-24 w-auto transition-all duration-500" data-id="element-3" />
        </Link>

        {/* Desktop Navigation - Hidden on medium screens to prevent overflow, visible on large */}
        <div className="hidden lg:flex items-center space-x-8" data-id="element-4">
          {['About Us', 'Services', 'Blog', 'Contacts'].map(item => {
          const path = item === 'About Us' ? '/about' : item === 'Contacts' ? '/contact' : `/${item.toLowerCase()}`;
          const isActive = location.pathname === path;
          return <Link key={item} to={path} className={cn('text-sm font-medium tracking-wide hover:opacity-70 transition-opacity relative', isScrolled ? 'text-charcoal' : 'text-white', isActive && (isScrolled ? 'text-warmCoral' : 'text-lightBlue'))} data-id="element-5">
                {item}
                {isActive && <motion.div layoutId="nav-underline" className={cn('absolute -bottom-1 left-0 right-0 h-0.5 rounded-full', isScrolled ? 'bg-warmCoral' : 'bg-lightBlue')} data-id="element-6" />}
              </Link>;
        })}
          <Link to="/contact" className={cn('px-6 py-2.5 rounded-full text-sm font-medium transition-all inline-block', isScrolled ? 'bg-charcoal text-white hover:bg-charcoal/90' : 'bg-white text-charcoal hover:bg-white/90')} data-id="element-7">
            Book a Tour
          </Link>
        </div>

        {/* Tablet Navigation - Simplified for medium screens */}
        <div className="hidden md:flex lg:hidden items-center space-x-4" data-id="element-8">
          <Link to="/contact" className={cn('px-4 py-2 rounded-full text-xs font-medium transition-all inline-block', isScrolled ? 'bg-charcoal text-white hover:bg-charcoal/90' : 'bg-white text-charcoal hover:bg-white/90')} data-id="element-9">
            Book a Tour
          </Link>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2" data-id="element-10">
            {mobileMenuOpen ? <X className={isScrolled ? 'text-charcoal' : 'text-white'} data-id="element-11" /> : <Menu className={isScrolled ? 'text-charcoal' : 'text-white'} data-id="element-12" />}
          </button>
        </div>

        {/* Mobile Toggle */}
        <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-13">
          {mobileMenuOpen ? <X className={isScrolled ? 'text-charcoal' : 'text-white'} data-id="element-14" /> : <Menu className={isScrolled ? 'text-charcoal' : 'text-white'} data-id="element-15" />}
        </button>
      </div>

      <AnimatePresence data-id="element-16">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="absolute top-full left-0 right-0 bg-white border-b border-warmGray/10 p-6 flex flex-col space-y-4 md:hidden" data-id="element-17">
            {['About Us', 'Services', 'Blog', 'Contacts'].map(item => {
          const path = item === 'About Us' ? '/about' : item === 'Contacts' ? '/contact' : `/${item.toLowerCase()}`;
          return <Link key={item} to={path} onClick={() => setMobileMenuOpen(false)} className="text-charcoal text-lg font-serif" data-id="element-18">
                  {item}
                </Link>;
        })}
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="bg-warmCoral text-white py-3 rounded-xl font-medium text-center" data-id="element-19">
              Book a Tour
            </Link>
          </motion.div>}
      </AnimatePresence>
    </nav>;
};
const Hero = () => {
  return <section className="relative h-screen min-h-[700px] flex items-center overflow-hidden" data-id="element-20">
      <div className="absolute inset-0 z-0" data-id="element-21">
        <video src="https://cdn.magicpatterns.com/uploads/r6it7hivDjyEAxoQgHF4ju/7803499-hd_1280_720_25fps.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-22" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" data-id="element-23" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full grid md:grid-cols-2 gap-12 items-center pt-20 md:pt-0" data-id="element-24">
        <motion.div initial={{
        opacity: 0,
        x: -30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="text-center md:text-left" data-id="element-25">
          <span className="inline-block text-lightBlue font-accent text-2xl md:text-3xl mb-4" data-id="element-26">
            Home away from Home
          </span>
          <h1 className="text-white text-4xl md:text-6xl lg:text-7xl font-serif leading-[1.1] mb-6 md:mb-8 break-words" data-id="element-27">
            Care for Every <br className="hidden md:block" data-id="element-28" /> Situation
          </h1>
          <p className="text-white/80 text-base md:text-lg max-w-md mx-auto md:mx-0 mb-8 md:mb-10 font-light leading-relaxed" data-id="element-29">
            Experience a new standard of luxury assisted living where dignity,
            comfort, and professional care meet in a serene, resort-style
            environment.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4" data-id="element-30">
            <Link to="/contact" className="w-full sm:w-auto bg-warmCoral hover:bg-warmCoral/90 text-white px-10 py-4 rounded-full text-lg font-medium transition-all text-center" data-id="element-31">
              Contact Us
            </Link>
            <Link to="/services" className="w-full sm:w-auto bg-lightBlue/20 hover:bg-lightBlue/40 backdrop-blur-md text-white border border-lightBlue/30 px-10 py-4 rounded-full text-lg font-medium transition-all text-center" data-id="element-32">
              Explore Services
            </Link>
          </div>
        </motion.div>

        <div className="hidden md:grid grid-cols-1 gap-6 justify-items-end pr-4 lg:pr-0" data-id="element-33">
          {[{
          icon: Clock,
          label: '24/7 Care',
          sub: 'Round-the-clock support'
        }, {
          icon: Star,
          label: '10+ Years',
          sub: 'Excellence in service'
        }, {
          icon: Heart,
          label: 'Whole Person',
          sub: 'Mind, body, and soul'
        }].map((stat, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5 + i * 0.1
        }} className="bg-charcoal/60 backdrop-blur-md border border-white/10 shadow-2xl p-6 rounded-2xl w-64 flex items-center space-x-4" data-id="element-34">
              <div className={cn('p-3 rounded-xl', i % 2 === 0 ? 'bg-warmCoral/20 text-warmCoral' : 'bg-lightBlue/20 text-lightBlue')} data-id="element-35">
                <stat.icon className="w-6 h-6" data-id="element-36" />
              </div>
              <div data-id="element-37">
                <div className="text-white font-serif text-lg" data-id="element-38">
                  {stat.label}
                </div>
                <div className="text-white/60 text-xs uppercase tracking-widest" data-id="element-39">
                  {stat.sub}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
const ProfessionalCare = () => {
  return <section className="py-16 md:py-24 px-4 md:px-8 bg-white overflow-hidden" data-id="element-40">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 md:gap-20 items-center" data-id="element-41">
        <div className="relative" data-id="element-42">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="rounded-3xl overflow-hidden aspect-[4/5] relative z-10" data-id="element-43">
            <img src="https://cdn.magicpatterns.com/uploads/4YdmXKPXcqDcFezjSaBxxE/Gemini_Generated_Image_v6g2vbv6g2vbv6g2.png" alt="Professional Care" className="w-full h-full object-cover" data-id="element-44" />
          </motion.div>
          <div className="absolute -bottom-10 -right-0 w-64 h-64 bg-lightBlue/20 rounded-full blur-3xl -z-0" data-id="element-45" />
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="absolute -bottom-6 left-0 bg-warmDark text-white p-8 rounded-2xl shadow-2xl z-20 hidden lg:block" data-id="element-46">
            <div className="space-y-4" data-id="element-47">
              <div className="flex items-center space-x-3" data-id="element-48">
                <div className="w-2 h-2 bg-lightBlue rounded-full" data-id="element-49" />
                <span className="text-sm tracking-widest uppercase opacity-60" data-id="element-50">
                  Trust Metrics
                </span>
              </div>
              <div className="grid grid-cols-1 gap-4" data-id="element-51">
                <div className="flex items-center space-x-4" data-id="element-52">
                  <Clock className="w-5 h-5 text-lightBlue" data-id="element-53" />
                  <span className="font-serif" data-id="element-54">24 Hour Care</span>
                </div>
                <div className="flex items-center space-x-4" data-id="element-55">
                  <Users className="w-5 h-5 text-warmCoral" data-id="element-56" />
                  <span className="font-serif" data-id="element-57">Professional Team</span>
                </div>
                <div className="flex items-center space-x-4" data-id="element-58">
                  <MapPin className="w-5 h-5 text-lightBlue" data-id="element-59" />
                  <span className="font-serif" data-id="element-60">Hagerstown, Maryland</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center md:text-left" data-id="element-61">
          <span className="text-warmCoral font-medium tracking-widest uppercase text-sm mb-4 block" data-id="element-62">
            We Provide Care
          </span>
          <h2 className="text-charcoal text-4xl md:text-6xl font-serif mb-6 md:mb-8 leading-tight" data-id="element-63">
            Professional Care <br className="hidden md:block" data-id="element-64" /> You Can Trust
          </h2>
          <p className="text-charcoal/70 text-base md:text-lg leading-relaxed mb-8 md:mb-10" data-id="element-65">
            At Elisabeth House, we believe that senior living should be defined
            by dignity and grace. Our medical professionals and caregivers are
            dedicated to providing a seamless blend of high-end hospitality and
            comprehensive clinical support.
          </p>
          <div className="space-y-6 mb-10" data-id="element-66">
            <div className="flex items-start space-x-4" data-id="element-67">
              <div className="mt-1 bg-lightBlue/20 p-2 rounded-lg" data-id="element-68">
                <Mail className="w-5 h-5 text-lightBlue" data-id="element-69" />
              </div>
              <div data-id="element-70">
                <div className="text-charcoal/40 text-sm uppercase tracking-wider" data-id="element-71">
                  Inquiries
                </div>
                <div className="text-charcoal text-xl font-serif" data-id="element-72">
                  info@elisabethhouse.net
                </div>
              </div>
            </div>
          </div>
          <button className="group flex items-center justify-center md:justify-start space-x-3 text-lightBlue font-medium text-lg w-full md:w-auto" data-id="element-73">
            <span data-id="element-74">Learn more about our approach</span>
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-75" />
          </button>
        </motion.div>
      </div>
    </section>;
};
const SignatureExperience = () => {
  const services = [{
    icon: Heart,
    title: 'Individualised Care',
    slug: 'individualized-care',
    desc: 'Tailored wellness plans designed for unique needs.'
  }, {
    icon: Clock,
    title: 'Respite Care',
    slug: 'respite-care',
    desc: 'Short-term stays with full access to our luxury amenities.'
  }, {
    icon: Calendar,
    title: 'Trips & Outings',
    slug: 'trips-and-outing-activities',
    desc: 'Curated cultural experiences and social excursions.'
  }, {
    icon: Utensils,
    title: 'Assistance with Eating',
    slug: 'assisting-with-eating',
    desc: 'Dignified support with gourmet dining experiences.'
  }, {
    icon: Coffee,
    title: 'Dressing Support',
    slug: 'dressing',
    desc: 'Professional assistance with daily morning routines.'
  }, {
    icon: Tv,
    title: 'Cable TV + WiFi',
    slug: 'electronics-and-wifi',
    desc: 'High-speed connectivity and premium entertainment.'
  }];
  return <section id="services" className="py-16 md:py-24 px-4 md:px-8 bg-warmGray/5" data-id="element-76">
      <div className="max-w-7xl mx-auto" data-id="element-77">
        <div className="text-center mb-12 md:mb-20" data-id="element-78">
          <h2 className="text-charcoal text-3xl md:text-5xl font-serif mb-6" data-id="element-79">
            Care Designed Around Life
          </h2>
          <p className="text-charcoal/60 max-w-2xl mx-auto text-lg" data-id="element-80">
            Our services are crafted to provide the perfect balance of
            independence and support, delivered with the refinement of a luxury
            hotel.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-81">
          {services.map((service, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.1
        }} viewport={{
          once: true
        }} whileHover={{
          y: -10
        }} className="bg-white p-10 rounded-3xl border border-warmGray/10 shadow-sm hover:shadow-xl transition-all duration-500" data-id="element-82">
              <div className={cn('w-16 h-16 rounded-2xl flex items-center justify-center mb-8', i % 2 === 0 ? 'bg-softPeach/20 text-warmCoral' : 'bg-lightBlue/20 text-lightBlue')} data-id="element-83">
                <service.icon className="w-8 h-8" data-id="element-84" />
              </div>
              <h3 className="text-charcoal text-2xl font-serif mb-4" data-id="element-85">
                {service.title}
              </h3>
              <p className="text-charcoal/60 leading-relaxed mb-6" data-id="element-86">
                {service.desc}
              </p>
              <Link to={`/services/${service.slug}`} className="inline-flex items-center space-x-2 text-charcoal font-medium hover:text-warmCoral transition-colors group/link" data-id="element-87">
                <span data-id="element-88">Learn more</span>
                <ChevronRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" data-id="element-89" />
              </Link>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
const ImmersiveDark = () => {
  return <section className="relative h-[500px] md:h-[600px] flex items-center justify-center overflow-hidden" data-id="element-90">
      <div className="absolute inset-0" data-id="element-91">
        <img src="https://elisabethhouse.net/wp-content/uploads/2020/04/thoughtful-disabled-senior-man-sitting-on-wheelcha-PRNGKS8-scaled.jpg" alt="Senior Man" className="w-full h-full object-cover" data-id="element-92" />
        <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-[2px]" data-id="element-93" />
      </div>
      <div className="relative z-10 text-center px-6" data-id="element-94">
        <motion.span initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} className="text-lightBlue tracking-[0.3em] uppercase text-sm mb-6 block" data-id="element-95">
          We are always close by
        </motion.span>
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} className="text-white text-3xl md:text-7xl font-serif mb-8 md:mb-12 max-w-4xl mx-auto leading-tight" data-id="element-96">
          Competent Assisted Living Services
        </motion.h2>
        <motion.div whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} data-id="element-97">
          <Link to="/contact" className="bg-white text-charcoal px-12 py-5 rounded-full text-lg font-medium hover:bg-warmCoral hover:text-white transition-all duration-300 shadow-2xl inline-block" data-id="element-98">
            Schedule a Private Tour
          </Link>
        </motion.div>
      </div>
    </section>;
};
const WhyChoose = () => {
  const features = [{
    title: 'Emotional Care',
    desc: 'Meeting emotional needs through companionship, empathy, and a vibrant community life that celebrates every individual.',
    image: 'https://cdn.magicpatterns.com/uploads/kh5n1pimpVW5TGv2ZwcUBX/ChatGPT_Image_Jun_30,_2026,_11_52_33_PM.png',
    label: 'Connection'
  }, {
    title: 'Physical Wellness',
    desc: 'Professional nursing support combined with holistic wellness programs, nutritious dining, and active lifestyle choices.',
    image: 'https://cdn.magicpatterns.com/uploads/uUn8VpymKc6LYc27Hd1E9R/4.png',
    label: 'Vitality',
    reverse: true
  }, {
    title: 'Independent Living',
    desc: 'Comfort with dignity. We provide the support you need while honoring your autonomy and personal lifestyle preferences.',
    image: 'https://elisabethhouse.net/wp-content/uploads/2020/04/5.png',
    label: 'Freedom'
  }];
  return <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-id="element-99">
      <div className="max-w-7xl mx-auto space-y-20 md:space-y-32" data-id="element-100">
        {features.map((feature, i) => <div key={i} className={cn('grid md:grid-cols-2 gap-12 md:gap-20 items-center', feature.reverse && 'md:flex-row-reverse')} data-id="element-101">
            <motion.div initial={{
          opacity: 0,
          x: feature.reverse ? 50 : -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className={cn('relative', feature.reverse && 'md:order-2')} data-id="element-102">
              <div className="rounded-3xl overflow-hidden aspect-[16/10] shadow-2xl" data-id="element-103">
                <img src={feature.image} alt={feature.title} className="w-full h-full object-cover" data-id="element-104" />
              </div>
              <div className="absolute -top-6 -right-6 bg-warmCoral text-white px-6 py-2 rounded-full font-accent text-2xl shadow-lg" data-id="element-105">
                {feature.label}
              </div>
            </motion.div>
            <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className={cn('text-center md:text-left', feature.reverse && 'md:order-1')} data-id="element-106">
              <h3 className="text-charcoal text-3xl md:text-5xl font-serif mb-6" data-id="element-107">
                {feature.title}
              </h3>
              <p className="text-charcoal/60 text-lg md:text-xl leading-relaxed mb-8" data-id="element-108">
                {feature.desc}
              </p>
              <Link to="/about" className="text-warmCoral font-medium flex items-center justify-center md:justify-start space-x-2 group" data-id="element-109">
                <span data-id="element-110">Explore this pillar</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-111" />
              </Link>
            </motion.div>
          </div>)}
      </div>
    </section>;
};
const AboutSection = ({
  initialTab = 0
}: {
  initialTab?: number;
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => {
    if (initialTab !== undefined) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  const content = [{
    id: 'About Center',
    title: 'A Sanctuary of Sophistication',
    text: "Elisabeth House is more than a residence; it's a carefully curated environment where every detail is designed to inspire peace and well-being. From our architectural gardens to our private suites, we offer a sanctuary for those who appreciate the finer things in life.",
    image: 'https://elisabethhouse.net/wp-content/uploads/2020/04/2.png'
  }, {
    id: 'Main Approach',
    title: 'The Art of Person-Centered Care',
    text: "Our philosophy centers on the 'Whole Person' approach. We don't just treat conditions; we nurture spirits. By understanding each resident's life story, preferences, and aspirations, we create a living experience that is deeply personal and profoundly meaningful.",
    image: 'https://elisabethhouse.net/wp-content/uploads/2020/04/3.png'
  }, {
    id: 'What We Offer',
    title: 'Unrivaled Amenities & Services',
    text: 'Enjoy gourmet dining prepared by executive chefs, a full-service wellness spa, concierge transportation, and a calendar of cultural events that rivals any luxury resort. Our clinical excellence is seamlessly integrated into a lifestyle of abundance.',
    image: 'https://elisabethhouse.net/wp-content/uploads/2017/10/bg-14-copyright.jpg'
  }, {
    id: 'How We Work',
    title: 'A Partnership of Trust',
    text: 'We work hand-in-hand with families and medical professionals to ensure a continuous circle of care. Transparency, communication, and unwavering commitment to excellence are the cornerstones of our operational excellence.',
    image: 'https://elisabethhouse.net/wp-content/uploads/2017/10/bg-13-copyright.jpg'
  }];
  return <section id="about" className="py-16 md:py-24 bg-charcoal text-white overflow-hidden" data-id="element-112">
      <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-113">
        <div className="flex flex-wrap justify-center md:justify-start gap-2 md:gap-4 mb-12 md:mb-16 border-b border-white/10 pb-8" data-id="element-114">
          {content.map((item, i) => <button key={item.id} onClick={() => setActiveTab(i)} className={cn('text-lg font-serif px-6 py-2 transition-all relative', activeTab === i ? 'text-warmCoral' : 'text-white/40 hover:text-white/70')} data-id="element-115">
              {item.id}
              {activeTab === i && <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-warmCoral" data-id="element-116" />}
            </button>)}
        </div>

        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center min-h-[400px] md:min-h-[500px]" data-id="element-117">
          <AnimatePresence mode="wait" data-id="element-118">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} transition={{
            duration: 0.5
          }} className="text-center md:text-left" data-id="element-119">
              <h3 className="text-3xl md:text-5xl font-serif mb-6 md:mb-8 leading-tight" data-id="element-120">
                {content[activeTab].title}
              </h3>
              <p className="text-white/60 text-lg md:text-xl leading-relaxed mb-8 md:mb-10" data-id="element-121">
                {content[activeTab].text}
              </p>
              <Link to="/about" state={{
              activeTab
            }} className="bg-white text-charcoal px-8 py-3 rounded-full font-medium hover:bg-warmCoral hover:text-white transition-all inline-block w-full sm:w-auto" data-id="element-122">
                Learn More
              </Link>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait" data-id="element-123">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            scale: 1.1
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            duration: 0.7
          }} className="relative aspect-square rounded-3xl overflow-hidden" data-id="element-124">
              <img src={content[activeTab].image} alt={content[activeTab].id} className="w-full h-full object-cover" data-id="element-125" />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent" data-id="element-126" />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
};
const Testimonials = () => {
  const reviews = [{
    quote: 'Finding Elisabeth House was a blessing. The level of care and the beauty of the environment gave our family peace of mind we never thought possible.',
    author: 'Sarah Montgomery',
    role: 'Daughter of Resident',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=200'
  }, {
    quote: "The staff doesn't just provide care; they provide friendship and dignity. My father feels like he's living in a five-star hotel with family.",
    author: 'James Harrison',
    role: 'Son of Resident',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=200'
  }];
  return <section className="py-16 md:py-24 bg-warmDark text-white overflow-hidden" data-id="element-127">
      <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-128">
        <div className="text-center mb-12 md:mb-20" data-id="element-129">
          <span className="text-warmCoral font-accent text-2xl md:text-3xl mb-4 block" data-id="element-130">
            What Families Say
          </span>
          <h2 className="text-3xl md:text-5xl font-serif" data-id="element-131">
            Stories of Care & Trust
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12" data-id="element-132">
          {reviews.map((review, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.1
        }} viewport={{
          once: true
        }} whileHover={{
          y: -5
        }} className="bg-white/5 backdrop-blur-lg border border-white/10 p-8 md:p-12 rounded-[32px] md:rounded-[40px] relative" data-id="element-133">
              <div className="flex text-warmCoral mb-8" data-id="element-134">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-135" />)}
              </div>
              <p className="text-2xl font-serif italic mb-10 leading-relaxed text-white/90" data-id="element-136">
                "{review.quote}"
              </p>
              <div className="flex items-center space-x-4" data-id="element-137">
                <img src={review.image} alt={review.author} className="w-14 h-14 rounded-full object-cover grayscale" data-id="element-138" />
                <div data-id="element-139">
                  <div className="font-serif text-lg" data-id="element-140">{review.author}</div>
                  <div className="text-white/40 text-sm uppercase tracking-widest" data-id="element-141">
                    {review.role}
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
const Team = () => {
  const members = [{
    name: 'Dr. Elena Vance',
    role: 'Chief Medical Officer',
    image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&q=80&w=400'
  }, {
    name: 'Marcus Thorne',
    role: 'Executive Director',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=400'
  }, {
    name: 'Julianne Moore',
    role: 'Head of Care',
    image: 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?auto=format&fit=crop&q=80&w=400'
  }, {
    name: 'Robert Sterling',
    role: 'CEO & Founder',
    image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=400'
  }];
  return <section id="team" className="py-16 md:py-24 px-4 md:px-8 bg-white" data-id="element-142">
      <div className="max-w-7xl mx-auto" data-id="element-143">
        <div className="flex flex-col md:flex-row justify-between items-center md:items-end gap-6 mb-12 md:mb-16" data-id="element-144">
          <div className="text-center md:text-left" data-id="element-145">
            <span className="text-warmCoral tracking-widest uppercase text-sm mb-4 block" data-id="element-146">
              Our Leadership
            </span>
            <h2 className="text-3xl md:text-5xl font-serif" data-id="element-147">Meet Our Team</h2>
          </div>
          <button className="hidden md:block text-charcoal font-medium border-b border-charcoal pb-1" data-id="element-148">
            View All Staff
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8" data-id="element-149">
          {members.map((member, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.1
        }} viewport={{
          once: true
        }} whileHover={{
          y: -10
        }} className="group cursor-pointer" data-id="element-150">
              <div className="aspect-[3/4] rounded-3xl overflow-hidden mb-6 relative" data-id="element-151">
                <img src={member.image} alt={member.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-152" />
                <div className="absolute inset-0 bg-warmDark/20 group-hover:bg-transparent transition-colors" data-id="element-153" />
              </div>
              <h4 className="text-charcoal text-xl font-serif mb-1" data-id="element-154">
                {member.name}
              </h4>
              <p className="text-charcoal/40 text-sm uppercase tracking-widest" data-id="element-155">
                {member.role}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
const Footer = () => {
  return <footer className="bg-charcoal text-white py-16 md:py-20 px-4 md:px-8 border-t border-white/5" data-id="element-156">
      <div className="max-w-7xl mx-auto" data-id="element-157">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16 md:mb-20" data-id="element-158">
          <div className="md:col-span-2 text-center md:text-left" data-id="element-159">
            <img src="https://cdn.magicpatterns.com/uploads/mhUq4NyA1Ppgv9PPq8CSnF/ChatGPT_Image_Jun_30,_2026,_11_49_14_PM.png" alt="Elisabeth's House Logo" className="h-16 md:h-20 w-auto mb-6 md:mb-8 mx-auto md:mx-0" data-id="element-160" />
            <p className="text-white/40 max-w-sm mx-auto md:mx-0 text-base md:text-lg leading-relaxed" data-id="element-161">
              Redefining senior living through the lens of luxury, dignity, and
              unparalleled professional care.
            </p>
          </div>
          <div className="text-center md:text-left" data-id="element-162">
            <h5 className="text-sm uppercase tracking-[0.2em] font-medium mb-6 md:mb-8 text-warmCoral" data-id="element-163">
              Navigation
            </h5>
            <ul className="space-y-4 text-white/60" data-id="element-164">
              <li data-id="element-165">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-166">
                  Home
                </Link>
              </li>
              <li data-id="element-167">
                <Link to="/services" className="hover:text-white transition-colors" data-id="element-168">
                  Services
                </Link>
              </li>
              <li data-id="element-169">
                <Link to="/contact" className="hover:text-white transition-colors" data-id="element-170">
                  Contacts
                </Link>
              </li>
            </ul>
          </div>
          <div className="text-center md:text-left" data-id="element-171">
            <h5 className="text-sm uppercase tracking-[0.2em] font-medium mb-6 md:mb-8 text-warmCoral" data-id="element-172">
              Connect
            </h5>
            <div className="flex justify-center md:justify-start space-x-6 mb-8" data-id="element-173">
              <Instagram className="w-6 h-6 text-white/40 hover:text-white cursor-pointer" data-id="element-174" />
              <Facebook className="w-6 h-6 text-white/40 hover:text-white cursor-pointer" data-id="element-175" />
              <Linkedin className="w-6 h-6 text-white/40 hover:text-white cursor-pointer" data-id="element-176" />
            </div>
            <p className="text-white/40 text-sm" data-id="element-177">
              Copyright © 2026 by Codelab LLC. <br data-id="element-178" /> All rights reserved.
            </p>
          </div>
        </div>
        <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-179">
          <div className="flex space-x-8 text-xs uppercase tracking-widest text-white/20" data-id="element-180">
            <a href="#" className="hover:text-white" data-id="element-181">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white" data-id="element-182">
              Terms of Service
            </a>
            <a href="#" className="hover:text-white" data-id="element-183">
              Accessibility
            </a>
          </div>
          <div className="text-white/20 text-xs uppercase tracking-[0.3em]" data-id="element-184">
            Designed for Excellence
          </div>
        </div>
      </div>
    </footer>;
};
// --- Main App ---
const LandingPage = ({
  aboutTab
}: {
  aboutTab?: number;
}) => <main data-id="element-185">
    <Hero data-id="element-186" />
    <ProfessionalCare data-id="element-187" />
    <SignatureExperience data-id="element-188" />
    <ImmersiveDark data-id="element-189" />
    <WhyChoose data-id="element-190" />
    <AboutSection initialTab={aboutTab} data-id="element-191" />
    <Testimonials data-id="element-192" />
    <Team data-id="element-193" />
  </main>;
const ScrollToTop = () => {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [pathname]);
  return null;
};
export function App() {
  const screenInit = useScreenInit();
  return <div className="min-h-screen bg-white overflow-x-clip" data-id="element-194">
      <ScrollToTop data-id="element-195" />
      <Navbar data-id="element-196" />
      <Routes data-id="element-197">
        <Route path="/" element={<LandingPage aboutTab={screenInit.state?.aboutTab} data-id="element-199" />} data-id="element-198" />
        <Route path="/about" element={<AboutUs data-id="element-201" />} data-id="element-200" />
        <Route path="/services" element={<Services data-id="element-203" />} data-id="element-202" />
        <Route path="/services/:slug" element={<ServiceDetails data-id="element-205" />} data-id="element-204" />
        <Route path="/contact" element={<ContactUs data-id="element-207" />} data-id="element-206" />
        <Route path="/blog" element={<Blog data-id="element-209" />} data-id="element-208" />
        <Route path="/blog/:id" element={<BlogDetails data-id="element-211" />} data-id="element-210" />
      </Routes>
      <Footer data-id="element-212" />
    </div>;
}